import type { TFunction } from '@ops-matrix/shared-types';

type SortableNavItem = {
  label: string;
  // Two field names exist for the same concept:
  //   `sectionHeader` — used by SidebarPanel's promoted PanelItem (after it maps
  //                     NavTab.section → sectionHeader at projection time).
  //   `section`       — used by NavTab itself, the shape PortalLayout passes
  //                     directly when sorting tab-promotion candidates.
  // Both must be honored so PortalLayout's rail-click sort produces the same
  // ordering as SidebarPanel's render-time sort. Otherwise the rail navigates
  // to a different "first" tab than the one the panel highlights.
  sectionHeader?: string;
  section?: string;
};

/**
 * Sort secondary-nav items alphabetically (A→Z) by their translated label,
 * with grouping preserved.
 *
 * Items carrying a sub-group field (`sectionHeader` on PanelItem, or `section`
 * on NavTab) are bucketed by that group header. Items WITHOUT a group form a
 * single "ungrouped" bucket. Output ordering:
 *
 *   1. Ungrouped bucket FIRST (so a default-landing entry like "Overview" that
 *      intentionally has no section header pins to the top of the panel).
 *   2. All grouped buckets, ordered by their TRANSLATED group header A→Z.
 *   3. Within each bucket: items sorted A→Z by translated label.
 *
 * Why ungrouped-first: Control Center declares `Overview` as the default
 * `/control-center` landing tab with no section. After we added alphabetical
 * group sort, an "ungrouped" bucket would otherwise drop between "Organization"
 * and "Workflows" once `Organization` exists — Overview belongs at the top of
 * the panel, not the middle. Treating ungrouped as the implicit top bucket is
 * the simplest invariant that satisfies the layout requirement without needing
 * a separate "isDefault" flag on every nav config.
 *
 * Sort is locale-aware via `localeCompare` so non-English translations sort
 * correctly in the user's locale.
 */
export function sortItemsByTranslatedLabel<T extends SortableNavItem>(items: T[], t: TFunction): T[] {
  if (items.length <= 1) return items;

  const groups: { header: string | undefined; items: T[] }[] = [];
  const groupIndex = new Map<string | undefined, number>();

  for (const item of items) {
    const header = item.sectionHeader ?? item.section;
    let idx = groupIndex.get(header);
    if (idx === undefined) {
      idx = groups.length;
      groupIndex.set(header, idx);
      groups.push({ header, items: [] });
    }
    groups[idx].items.push(item);
  }

  for (const group of groups) {
    group.items.sort((a, b) => {
      const aLabel = (t(a.label) || a.label).toLowerCase();
      const bLabel = (t(b.label) || b.label).toLowerCase();
      return aLabel.localeCompare(bLabel);
    });
  }

  // Stable order: ungrouped first, then grouped buckets alphabetical by
  // translated header. `localeCompare` matches the within-group sort so the
  // user sees one consistent A→Z rule.
  groups.sort((a, b) => {
    if (a.header === undefined && b.header === undefined) return 0;
    if (a.header === undefined) return -1;
    if (b.header === undefined) return 1;
    const aHeader = (t(a.header) || a.header).toLowerCase();
    const bHeader = (t(b.header) || b.header).toLowerCase();
    return aHeader.localeCompare(bHeader);
  });

  return groups.flatMap((g) => g.items);
}
